/**
 * @fileoverview Helper simplificado para consultas de colecciones relacionadas
 * Consultas directas a PocketBase sin caché para máxima simplicidad y consistencia
 */

import { pb } from '../../helpers/pocketbase_helper';
import { RepositoryError } from '../../types/common/PocketBaseTypes';

/**
 * Helper class para cargar datos relacionados de forma simple y directa
 */
export class RelatedDataHelper {

  /**
   * Carga todos los clientes activos
   */
  static async getClientes(): Promise<any[]> {
    try {
      return await pb.collection("cliente").getFullList({ 
        filter: "activo=true",
        sort: "razon_social"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar clientes: ${error instanceof Error ? error.message : String(error)}`,
        'CLIENTES_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los usuarios/asesores comerciales
   */
  static async getUsuarios(): Promise<any[]> {
    try {
      return await pb.collection("users").getFullList({
        sort: "name"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar usuarios: ${error instanceof Error ? error.message : String(error)}`,
        'USUARIOS_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los materiales activos
   */
  static async getMateriales(): Promise<any[]> {
    try {
      return await pb.collection("material").getFullList({ 
        filter: "activo=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar materiales: ${error instanceof Error ? error.message : String(error)}`,
        'MATERIALES_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los productos activos
   */
  static async getProductos(): Promise<any[]> {
    try {
      return await pb.collection("producto").getFullList({ 
        filter: "activo=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar productos: ${error instanceof Error ? error.message : String(error)}`,
        'PRODUCTOS_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los tipos de solicitud activos
   */
  static async getTiposSolicitud(): Promise<any[]> {
    try {
      return await pb.collection("tipo_solicitud").getFullList({ 
        filter: "activo=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar tipos de solicitud: ${error instanceof Error ? error.message : String(error)}`,
        'TIPOS_SOLICITUD_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los procesos disponibles (mapa_nombre_proceso_tabla)
   */
  static async getProcesos(): Promise<any[]> {
    try {
      return await pb.collection("mapa_nombre_proceso_tabla").getFullList({
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar procesos: ${error instanceof Error ? error.message : String(error)}`,
        'PROCESOS_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los tipos de pinza activos
   */
  static async getTiposPinza(): Promise<any[]> {
    try {
      return await pb.collection("tipo_pinza").getFullList({ 
        filter: "activo=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar tipos de pinza: ${error instanceof Error ? error.message : String(error)}`,
        'TIPOS_PINZA_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todas las medidas activas
   */
  static async getMedidas(): Promise<any[]> {
    try {
      return await pb.collection("medida").getFullList({ 
        filter: "activo=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar medidas: ${error instanceof Error ? error.message : String(error)}`,
        'MEDIDAS_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los recubrimientos
   */
  static async getRecubrimientos(): Promise<any[]> {
    try {
      return await pb.collection("recubrimiento").getFullList({
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar recubrimientos: ${error instanceof Error ? error.message : String(error)}`,
        'RECUBRIMIENTOS_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga tipos de recubrimiento
   */
  static async getTiposRecubrimiento(): Promise<any[]> {
    try {
      return await pb.collection("recubrimiento_tipo_recubrimiento").getFullList({
        sort: "tipo"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar tipos de recubrimiento: ${error instanceof Error ? error.message : String(error)}`,
        'TIPOS_RECUBRIMIENTO_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga parámetros de brillo interno activos
   */
  static async getParametrosBrillo(): Promise<any[]> {
    try {
      return await pb.collection("parametros_brillo_interno").getFullList({
        filter: "estado=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar parámetros de brillo: ${error instanceof Error ? error.message : String(error)}`,
        'PARAMETROS_BRILLO_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga parámetros de plastificado interno activos
   */
  static async getParametrosPlastificado(): Promise<any[]> {
    try {
      return await pb.collection("parametros_plastificado_interno").getFullList({
        filter: "estado=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar parámetros de plastificado: ${error instanceof Error ? error.message : String(error)}`,
        'PARAMETROS_PLASTIFICADO_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga parámetros de pegue interno activos
   */
  static async getParametrosPegue(): Promise<any[]> {
    try {
      return await pb.collection("parametros_pegue_interno").getFullList({
        filter: "estado=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar parámetros de pegue: ${error instanceof Error ? error.message : String(error)}`,
        'PARAMETROS_PEGUE_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga parámetros de troquelado interno activos
   */
  static async getParametrosTroquelado(): Promise<any[]> {
    try {
      return await pb.collection("parametros_troquelado_interno").getFullList({
        filter: "estado=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar parámetros de troquelado: ${error instanceof Error ? error.message : String(error)}`,
        'PARAMETROS_TROQUELADO_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga parámetros de acabado interno activos
   */
  static async getParametrosAcabado(): Promise<any[]> {
    try {
      return await pb.collection("parametros_acabado_interno").getFullList({
        filter: "estado=true",
        sort: "nombre"
      });
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar parámetros de acabado: ${error instanceof Error ? error.message : String(error)}`,
        'PARAMETROS_ACABADO_LOAD_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los parámetros de proceso en paralelo
   */
  static async getAllParametrosProceso(): Promise<{
    brillo: any[];
    plastificado: any[];
    pegue: any[];
    troquelado: any[];
    acabado: any[];
  }> {
    try {
      // Cargar parámetros en paralelo para máxima velocidad de carga inicial
      const [brillo, plastificado, pegue, troquelado, acabado] = await Promise.all([
        this.getParametrosBrillo(),
        this.getParametrosPlastificado(),
        this.getParametrosPegue(),
        this.getParametrosTroquelado(),
        this.getParametrosAcabado()
      ]);

      return {
        brillo,
        plastificado,
        pegue,
        troquelado,
        acabado
      };
    } catch (error) {
      console.error('❌ RelatedDataHelper: Error al cargar parámetros de proceso:', error);
      throw new RepositoryError(
        `Error al cargar parámetros de proceso: ${error instanceof Error ? error.message : String(error)}`,
        'LOAD_PARAMETROS_ERROR',
        error
      );
    }
  }

  /**
   * Carga todos los datos básicos del formulario en paralelo
   */
  static async getAllFormData(): Promise<{
    clientes: any[];
    usuarios: any[];
    materiales: any[];
    productos: any[];
    tiposSolicitud: any[];
    tiposPinza: any[];
    medidas: any[];
    recubrimientos: any[];
    tiposRecubrimiento: any[];
  }> {
    try {
      // Cargar datos en paralelo para máxima velocidad de carga inicial
      const [clientes, usuarios, materiales, productos, tiposSolicitud, tiposPinza, medidas, recubrimientos, tiposRecubrimiento] = await Promise.all([
        this.getClientes(),
        this.getUsuarios(),
        this.getMateriales(),
        this.getProductos(),
        this.getTiposSolicitud(),
        this.getTiposPinza(),
        this.getMedidas(),
        this.getRecubrimientos(),
        this.getTiposRecubrimiento()
      ]);

      return {
        clientes,
        usuarios,
        materiales,
        productos,
        tiposSolicitud,
        tiposPinza,
        medidas,
        recubrimientos,
        tiposRecubrimiento
      };
    } catch (error) {
      console.error('❌ RelatedDataHelper: Error al cargar datos:', error);
      throw new RepositoryError(
        `Error al cargar todos los datos de formulario: ${error instanceof Error ? error.message : String(error)}`,
        'ALL_FORM_DATA_LOAD_ERROR',
        error
      );
    }
  }



}
